import { NavLink, useLocation } from 'react-router-dom'
import { Home, Menu, PackageSearch, ShoppingBag, Store } from 'lucide-react'
import { useCart } from '@/context/CartContext'
import { ROUTES } from '@/constants'
import { cn } from '@/utils/cn'

/**
 * Phone-only tab bar, the way shopping apps (and the big BD shops' mobile
 * sites) do it: the four places a shopper goes most, always one tap away,
 * plus the full menu. Hidden from `md` up where the header carries all of
 * this, and on pages that dock their own action bar (product Buy Now,
 * checkout Place Order) via `body.hide-bottom-nav`.
 *
 * @param {{ onOpenMenu: () => void }} props
 */
export function BottomNav({ onOpenMenu }) {
  const { cartCount, openDrawer, isOpen: cartOpen } = useCart()
  const { pathname } = useLocation()

  const itemClass = (active) =>
    cn(
      'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 pb-1 pt-2 text-[11px] font-medium transition',
      active ? 'text-coral' : 'text-charcoal-600'
    )

  return (
    <nav
      aria-label="Quick navigation"
      className="bottom-nav pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-charcoal-100 bg-white/95 shadow-[0_-2px_12px_rgba(0,0,0,0.05)] backdrop-blur md:hidden"
    >
      <div className="flex h-14 items-stretch">
        <NavLink to={ROUTES.home} end className={({ isActive }) => itemClass(isActive)}>
          <Home size={22} />
          Home
        </NavLink>
        <NavLink to={ROUTES.shop} className={({ isActive }) => itemClass(isActive)}>
          <Store size={22} />
          Shop
        </NavLink>
        <button
          type="button"
          onClick={openDrawer}
          aria-label="Open cart"
          className={itemClass(cartOpen || pathname === ROUTES.cart)}
        >
          <span className="relative">
            <ShoppingBag size={22} />
            {cartCount > 0 && (
              <span className="absolute -right-2.5 -top-1.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-coral px-1 text-[10px] font-bold text-white">
                {cartCount}
              </span>
            )}
          </span>
          Cart
        </button>
        <NavLink to={ROUTES.trackOrder} className={({ isActive }) => itemClass(isActive)}>
          <PackageSearch size={22} />
          Track
        </NavLink>
        <button type="button" onClick={onOpenMenu} className={itemClass(false)} aria-label="Open menu">
          <Menu size={22} />
          Menu
        </button>
      </div>
    </nav>
  )
}
